import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, of, switchMap } from 'rxjs';
import { LivePipelineService } from './live-pipeline.service';
import { LivePipelineEntry, livePipelineKey } from '../models/live-pipeline.model';

/**
 * Die laufenden Pipeline-Durchlaeufe eines Projekts, fortgeschrieben ueber das
 * Projekt-Topic (NEX-1233).
 *
 * <p>Ein Abonnement je Ansicht und nicht eines je Zeile: eine Liste mit mehreren
 * laufenden Durchlaeufen oeffnete sonst ebenso viele Verbindungen. Der Broker
 * adressiert ohnehin je Projekt.
 *
 * <p>Gehalten werden nur die laufenden. Der Endpunkt liefert auch kurz zuvor
 * beendete, und das Topic meldet jeden Durchlauf ein letztes Mal mit seinem Ende —
 * beides muss den Eintrag entfernen, damit die Ansicht nicht auf dem letzten
 * laufenden Stand stehenbleibt.
 *
 * <p>Je Ansicht bereitgestellt, nicht wurzelweit: die Verbindung faellt mit der
 * Seite, die sie braucht.
 *
 * <p>Gefuehrt wird nach Durchlauf und nicht nach Testlauf: ein zweiter Start neben
 * einem laufenden Durchlauf ist erlaubt, ein Testlauf kann also zwei zugleich haben.
 * Nach Testlauf geschluesselt wuerden sie einander ueberschreiben, und das Ende des
 * einen naehme das Band des anderen mit.
 *
 * <p>Ein beobachteter Lauf ohne Testlauf faellt heraus (NEX-1292): das Band haengt unter
 * der Zeile seines Testlaufs, und ohne einen gibt es keine Zeile, unter die es gehoert.
 */
@Injectable()
export class LiveRunsStore {
  private readonly feed = inject(LivePipelineService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly entries = signal<ReadonlyMap<string, LivePipelineEntry>>(new Map());

  /**
   * Die laufenden Durchlaeufe nach Testlauf-Kennung. Hat ein Testlauf mehrere
   * zugleich, steht der zuletzt gestartete darin — die Ansicht hat je Zeile nur
   * einen Platz, und der gehoert dem juengsten Versuch.
   */
  readonly running: Signal<ReadonlyMap<string, LivePipelineEntry>> = computed(() => {
    const newestPerRun = new Map<string, LivePipelineEntry>();
    for (const entry of this.entries().values()) {
      const runId = entry.runId;
      if (runId === undefined) {
        continue;
      }
      const held = newestPerRun.get(runId);
      if (held === undefined || startedAfter(entry, held)) {
        newestPerRun.set(runId, entry);
      }
    }
    return newestPerRun;
  });

  private readonly endedRuns = new Subject<string>();

  /**
   * Die Testlauf-Kennung jedes Durchlaufs, dessen Ende das Topic meldet (NEX-1520). Der
   * Abschluss-Eintrag traegt den Endstatus, aber nicht die Zeile der Uebersicht mit Dauer
   * und Kennzahlen — die muss neu laden, sonst bleibt sie bis zum Neuladen auf „Laeuft".
   */
  readonly ended: Observable<string> = this.endedRuns.asObservable();

  private watching = false;

  /**
   * Laedt den Stand des Projekts und haengt sich an sein Topic. Ein zweiter Aufruf
   * bleibt wirkungslos — ein zweites Abonnement waere genau die Verbindung zu viel,
   * die dieser Store vermeiden soll.
   *
   * <p>Ein Fehler beim Laden kostet nur die Live-Anzeige: die Ansicht, die diesen
   * Store nutzt, steht fuer sich und darf nicht mit ihm ausfallen.
   *
   * <p>Nach jedem Reconnect wird der Stand einmal neu geladen und ersetzt: Ende-Meldungen
   * aus der Luecke sind verloren, und ohne Abgleich bliebe ein beendeter Lauf auf
   * „Laeuft" stehen.
   */
  watch(projectId: string): void {
    if (this.watching) {
      return;
    }
    this.watching = true;

    this.feed.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ projectId: source, entry }) => {
      if (source === projectId) {
        this.apply(entry);
      }
    });

    this.feed
      .list(projectId)
      .pipe(
        catchError(() => of([] as LivePipelineEntry[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((entries) => {
        this.replace(entries);
        this.feed.connect([projectId]);
      });

    // Ein misslungener Abgleich laesst den bisherigen Stand stehen: der ist hoechstens
    // veraltet, eine leere Liste dagegen waere falsch.
    this.feed.reconnected
      .pipe(
        switchMap(() => this.feed.list(projectId).pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((entries) => this.reconcile(entries));

    this.destroyRef.onDestroy(() => this.feed.disconnect());
  }

  private replace(entries: LivePipelineEntry[]): void {
    this.entries.set(new Map(entries.filter(isRunning).map((entry) => [livePipelineKey(entry), entry])));
  }

  /**
   * Abgleich nach einem Reconnect. Ein Lauf, der vorher lief und jetzt fehlt, hat in der
   * Luecke geendet — seine Ende-Meldung ist verloren, also meldet der Abgleich das Ende.
   */
  private reconcile(entries: LivePipelineEntry[]): void {
    const before = [...this.running().keys()];
    this.replace(entries);
    const after = this.running();
    for (const runId of before.filter((id) => !after.has(id))) {
      this.endedRuns.next(runId);
    }
  }

  private apply(entry: LivePipelineEntry): void {
    this.entries.update((current) => {
      const next = new Map(current);
      const key = livePipelineKey(entry);
      if (isRunning(entry)) {
        next.set(key, entry);
      } else {
        next.delete(key);
      }
      return next;
    });
    if (!isRunning(entry) && entry.runId !== undefined) {
      this.endedRuns.next(entry.runId);
    }
  }
}

function isRunning(entry: LivePipelineEntry): boolean {
  return entry.status === 'RUNNING';
}

/**
 * Ein unlesbarer Zeitstempel verliert gegen jeden lesbaren und gegen den bereits
 * gehaltenen Eintrag: {@code NaN} vergleicht sich mit allem als {@code false}.
 */
function startedAfter(entry: LivePipelineEntry, held: LivePipelineEntry): boolean {
  return Date.parse(entry.startedAt) > Date.parse(held.startedAt);
}
